import type { LayoutInfo } from "../types/templateConfiguration";
import type { IProduct2 } from "../types/general";
import customFetch from "../composables/customFetch";

export default defineNuxtRouteMiddleware(async (to, from) => {
  const config = useRuntimeConfig();
  const layout = config.public.layout as LayoutInfo;
  const localePath = useLocalePath();
  const isSupplier = isSupplierLayout(layout);
  const languagePrefixes = [
    "en",
    "fr",
    "es",
    "de",
    "ar",
    "ru",
    "tr",
    "az",
    "zh",
    "it",
    "he",
    "fa",
    "pt",
    "uz",
  ];
  // Strip the locale prefix so /en/things-to-do behaves like /things-to-do
  const [firstSegment, ...restSegments] = to.path.split("/").filter(Boolean);
  const barePath = languagePrefixes.includes(firstSegment)
    ? `/${restSegments.join("/")}`
    : to.path;
  if (barePath.startsWith("/discover")) {
    if (isSupplier) {
      return navigateTo(localePath("/experiences"));
    } else {
      return navigateTo(localePath("/things-to-do"));
    }
  } else if (barePath === "/experiences") {
    if (!isSupplier) {
      return navigateTo({ path: localePath("/things-to-do"), query: to.query });
    }
  } else if (barePath === "/things-to-do") {
    if (isSupplier) {
      return navigateTo({ path: localePath("/experiences"), query: to.query });
    }
  } else {
    const pathSegments = to.path.split("/").filter(Boolean);
    let citySlug, productSlug;
    const hasLang = languagePrefixes.some(
      (prefix) => pathSegments[0] === prefix,
    );
    if (
      (hasLang && pathSegments.length > 2) ||
      (!hasLang && pathSegments.length > 1)
    ) {
      if (hasLang) {
        [, citySlug, productSlug] = pathSegments;
      } else {
        [citySlug, productSlug] = pathSegments;
      }
      // /experiences/{slug} and /things-to-do-in-{city}/{slug} both resolve to the
      // same product page, which is duplicate content for SEO. Whichever one isn't
      // this site's canonical URL scheme gets a permanent (301) redirect to the
      // canonical one instead of being served directly.
      if (citySlug === "experiences" && productSlug && !isSupplier) {
        const cityForProduct = await resolveProductCitySlug(productSlug);
        if (cityForProduct) {
          return navigateTo(
            localePath(`/things-to-do-in-${cityForProduct}/${productSlug}`),
            { redirectCode: 301 },
          );
        }
        throw createError({ statusCode: 404, fatal: true });
      }

      if (citySlug?.startsWith("things-to-do-in-") && productSlug && isSupplier) {
        return navigateTo(localePath(`/experiences/${productSlug}`), {
          redirectCode: 301,
        });
      }

      if (citySlug?.startsWith("things-to-do-in-") && productSlug && !isSupplier) {
        const cityFromUrl = citySlug.replace("things-to-do-in-", "");
        const correctCity = await resolveProductCitySlug(productSlug);

        if (correctCity && correctCity !== cityFromUrl) {
          return navigateTo(
            localePath(`/things-to-do-in-${correctCity}/${productSlug}`),
            { redirectCode: 301 },
          );
        }
      }

      if (
        citySlug !== "blog" &&
        citySlug !== "experiences" &&
        citySlug !== "auth" &&
        citySlug !== "about" &&
        citySlug !== "cart" &&
        citySlug !== "checkout" &&
        citySlug !== "terms-and-conditions" &&
        citySlug !== "privacy-policy" &&
        citySlug !== "faq" &&
        citySlug !== "things-to-do" &&
        citySlug !== "search" &&
        citySlug !== "invoices" &&
        citySlug !== "coming-soon" &&
        !citySlug.startsWith("things-to-do-in")
      ) {
        if (isSupplier) {
          return navigateTo(`/experiences/${productSlug}`);
        } else {
          return navigateTo(`/things-to-do-in-${citySlug}/${productSlug}`);
        }
      }
    }
  }
});

async function resolveProductCitySlug(
  productSlug: string,
): Promise<string | null> {
  try {
    const product = await customFetch<IProduct2>(
      `api/v2/products/${productSlug}`,
    );
    return product?.cities?.[0]?.slug ?? null;
  } catch {
    return null;
  }
}
